import { Box, Card, CardContent, CardHeader, Typography } from '@mui/material'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Address, InvoiceSignature } from '../../../swagger'
import ImageDisplay from '../../Images/ImageDisplay'

export type InvoiceSignaturePreviewProps = {
  invoiceSignature: Pick<
    InvoiceSignature,
    'payableTo' | 'phoneNumber' | 'closingMessage' | 'email' | 'qrCodeImage'
  > & {
    address?: Pick<
      Partial<Address>,
      'streetAddress1' | 'streetAddress2' | 'city' | 'state' | 'zip'
    >
    isViewingInvoicePreview?: boolean
  }
}

export const InvoiceSignaturePreview: React.FC<InvoiceSignaturePreviewProps> = (
  props
) => {
  const { t } = useTranslation()
  const {
    payableTo: name,
    phoneNumber: phone,
    closingMessage,
    email,
    address,
    isViewingInvoicePreview = true,
    qrCodeImage,
  } = props.invoiceSignature
  const { streetAddress1, streetAddress2, city, state, zip } = address ?? {}
  const cityStateAndZip = (
    [city, state].filter(Boolean).join(', ') + (zip ? ` ${zip}` : '')
  ).trim()

  const Container: React.ElementType = isViewingInvoicePreview ? Card : Box

  return (
    <Container sx={{ maxWidth: isViewingInvoicePreview ? 400 : '100%' }}>
      {isViewingInvoicePreview && (
        <CardHeader
          title={t('InvoiceSignaturePreview.HeaderTitle', 'Signature Preview')}
        />
      )}

      <CardContent>
        <Box role="region" aria-label="invoiceSignaturePreview">
          <Typography variant="body1" mt={4} align="center">
            {t('InvoiceSignaturePreview.Name', 'Make Payable To: {{name}}', {
              name,
            })}
          </Typography>

          {qrCodeImage && (
            <ImageDisplay
              imageBase64={qrCodeImage}
              altText="QR Code"
              style={{
                display: 'flex',
                justifyContent: 'center',
                margin: '16px 0',
              }}
            />
          )}

          {closingMessage && (
            <Typography
              variant="h6"
              mt={2}
              align="center"
              sx={{ wordWrap: 'break-word' }}
            >
              {closingMessage}
            </Typography>
          )}

          <Box mt={2}>
            {streetAddress1 && (
              <Typography
                variant="body2"
                align="center"
                sx={{ wordWrap: 'break-word' }}
              >
                {streetAddress1}
              </Typography>
            )}

            {streetAddress2 && (
              <Typography variant="body2" align="center">
                {streetAddress2}
              </Typography>
            )}

            {cityStateAndZip && (
              <Typography variant="body2" align="center">
                {cityStateAndZip}
              </Typography>
            )}

            <Typography variant="body2" mt={2} align="center">
              {phone &&
                t('InvoiceSignaturePreview.Phone', 'Phone: {{phone}}', {
                  phone,
                })}
              {phone && email && ' | '}
              {email &&
                t('InvoiceSignaturePreview.Email', 'Email: {{email}}', {
                  email,
                })}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Container>
  )
}

export default InvoiceSignaturePreview
